'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState, useEffect } from 'react';

import { LanguageSwitcher } from '@/components/language-switcher';
import { ThemeToggle } from '@/components/theme-toggle';
import { useTranslations } from '@/components/language-provider';
import { useTheme } from '@/components/theme-provider';

const NAV_ITEMS = [
  { key: 'features', href: '/#features' },
  { key: 'workflow', href: '/#workflow' },
  { key: 'plans', href: '/subscription-plans' },
  { key: 'organizers', href: '/founding-organizers' },
] as const;

export function AppHeader() {
  const { messages } = useTranslations();
  const { actualTheme } = useTheme();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navItems = useMemo(
    () =>
      NAV_ITEMS.map((item) => ({
        ...item,
        label: messages.nav[item.key],
      })),
    [messages.nav],
  );

  const logoSrc = actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg';

  useEffect(() => {
    if (!mobileNavOpen) return;
    const handler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setMobileNavOpen(false);
        }
      };
    document.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = '';
    };
  }, [mobileNavOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-border/70 bg-background/95 backdrop-blur">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4 md:px-6">
        <Link href="/" className="flex items-center gap-3 text-lg font-semibold tracking-tight">
          <Image
            src={logoSrc}
            alt="WhoPlays US"
            width={188}
            height={52}
            priority
            className="h-13 w-auto"
          />
          <span className="text-2xl font-semibold tracking-tight">
            <span className="text-foreground">Who</span>
            <span className="ml-1 text-primary">Plays</span>
          </span>
        </Link>
        <div className="hidden items-center gap-6 text-sm font-medium text-muted-foreground md:flex">
          {navItems.map((item) => (
            <Link
              key={item.key}
              href={item.href}
              className="transition hover:text-foreground hover:underline hover:underline-offset-4"
            >
              {item.label}
            </Link>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-2 lg:flex">
            <div className="scale-90 origin-right">
              <LanguageSwitcher />
            </div>
            <div className="scale-90 origin-right">
              <ThemeToggle />
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Link
              href="/auth/login"
              className="hidden rounded-full px-3 py-1.5 text-muted-foreground transition hover:text-foreground sm:inline-flex"
            >
              {messages.nav.login}
            </Link>
            <Link
              href="/auth/signup"
              className="hidden rounded-full bg-gradient-to-r from-primary to-primary/80 px-4 py-2 text-sm font-semibold btn-primary shadow-md shadow-primary/40 transition hover:-translate-y-0.5 hover:shadow-lg md:inline-flex"
            >
              {messages.nav.signup}
            </Link>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full border border-border/70 p-2 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring md:hidden"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation"
          >
            <span className="sr-only">Open navigation</span>
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </nav>
      {mobileNavOpen ? (
        <div className="fixed inset-0 z-40 bg-black/60 md:hidden" aria-modal="true" role="dialog">
          <button
            type="button"
            className="absolute inset-0 h-full w-full"
            aria-label="Close navigation"
            onClick={() => setMobileNavOpen(false)}
          />
          <div className="ml-auto flex h-full w-64 max-w-[75vw] flex-col gap-6 border-l border-border/60 bg-background p-6 shadow-2xl">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-muted-foreground">Menu</span>
              <button
                type="button"
                className="rounded-full border border-border/70 p-2 text-sm transition hover:bg-muted/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                onClick={() => setMobileNavOpen(false)}
                aria-label="Close navigation"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                  <path
                    d="M6 6l12 12M18 6l-12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-4 text-sm font-semibold text-foreground bg-background/95">
              {navItems.map((item) => (
                <Link
                  key={item.key}
                  href={item.href}
                  className="rounded-xl border border-border/50 px-4 py-3 transition hover:bg-muted/50"
                  onClick={() => setMobileNavOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            <div className="flex flex-col gap-3 bg-background/95">
              <Link
                href="/auth/login"
                className="rounded-full border border-border/70 px-4 py-2 text-center text-sm font-semibold text-muted-foreground hover:text-foreground"
                onClick={() => setMobileNavOpen(false)}
              >
                {messages.nav.login}
              </Link>
              <Link
                href="/auth/signup"
                className="rounded-full bg-gradient-to-r from-primary to-primary/80 px-4 py-2 text-center text-sm font-semibold text-white shadow-md shadow-primary/40 hover:-translate-y-0.5 hover:shadow-lg"
                onClick={() => setMobileNavOpen(false)}
              >
                {messages.nav.signup}
              </Link>
            </div>
            <div className="flex items-center justify-between rounded-2xl border border-border/60 px-3 py-2">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
        </div>
      ) : null}
    </header>
  );
}
